import { createSlice, createSelector } from '@reduxjs/toolkit';
import {
  selectAllNodes,
  selectAllPoints,
  selectFilteredPoints,
  selectInterconnects,
  selectPriceComponents,
} from '../map/slice/mapSelectors';
import Fuse from 'fuse.js';
import { Interconnect, Point, PriceComponent } from '@/types';
import { LatLng } from '@/types/api/Edge';

export type SearchState = {
  isSearching: boolean;
  searchQuery: string;
  favorites: string[];
  itemType?: string;
  selectionKey?: string;
};

const initialState: SearchState = {
  isSearching: false,
  searchQuery: '',
  favorites: [],
};

export const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    searchingChanged: (state, action) => {
      const { isSearching, itemType, selectionKey } = action.payload;
      if (isSearching) {
        return {
          ...state,
          isSearching,
          itemType,
          selectionKey,
        };
      }
      return initialState;
    },
    queryChanged: (state, action) => ({
      ...state,
      searchQuery: action.payload.searchQuery,
    }),
  },
});

export const selectSearchState = (state: { search: SearchState }) =>
  state.search;

export const selectSearchQuery = createSelector(
  [selectSearchState],
  (search) => search.searchQuery ?? '',
);

// TODO: need to create an asyncronous search so it doesn't lag
// export const filterSearchResults = createAsyncThunk(
//   'search/filterSearchResults',
// )
// TODO: search favorites, filter favorites - they should probably be saved and retrieved from the backend on a per user basis
// TODO: this should select points / segments / edges, whatever.  Just starting with points for now
// also now that there's a filters slice, this selector has a confusing name
export const selectFilteredPointIds = createSelector(
  [selectFilteredPoints, selectSearchQuery],
  (points, _searchQuery) => {
    const searchQuery = !_searchQuery ? null : _searchQuery.toLowerCase();
    const allResults = Object.keys(points).filter((pointId) => {
      // console.log("ALL POINTS" + JSON.stringify(allPoints))
      // console.log("FILTERED POINTS" + JSON.stringify(points)

      const pointInfo = points[pointId];

      // PL-1404: Added name to Search to account for alias being different from loc_name.
      if (!searchQuery) return false;
      return (
        searchQuery === pointId ||
        searchQuery === `#${pointId}` ||
        pointInfo?.loc_name?.toLowerCase().includes(searchQuery) ||
        pointInfo?.name?.toLowerCase().includes(searchQuery) ||
        pointInfo?.loc_id?.toLowerCase().includes(searchQuery) ||
        pointInfo?.seg_nbr?.toString().includes(searchQuery) ||
        pointInfo?.loc_cnty?.toLowerCase().includes(searchQuery)
      );
    });
    // this makes search wayyy faster - just return the first x number of results
    return allResults.slice(0, 10);
  },
);

export type SearchElement = {
  searchFields: string[];
  id: number;
  type: 'point' | 'interconnect' | 'priceComponent';
  instance: Point | Interconnect | PriceComponent;
  location?: LatLng[];
  displayName: string;
  key: string;
};

export const selectSearchElements = createSelector(
  [selectAllPoints, selectAllNodes, selectInterconnects, selectPriceComponents],
  (points, nodes, interconnects, priceComponents): SearchElement[] => {
    const pointElements: SearchElement[] = Object.values(points).map(
      (point) => ({
        searchFields: [
          point.name,
          point.loc_name,
          point.id.toString(),
          point.loc_id,
          point.tsp_name,
          ...point.price_components.map((pc) => pc.description),
          ...point.capacity_components.map((cc) => cc.label ?? cc.description),
        ],
        id: Number(point.id),
        type: 'point',
        instance: point,
        location:
          point.node && nodes[point.node]
            ? [[Number(nodes[point.node].lat), Number(nodes[point.node].lng)]]
            : undefined,
        displayName: point.name,
        key: `point-${point.id}`,
      }),
    );
    const interconnectElements: SearchElement[] = Object.values(interconnects)
      .slice(0, 0)
      .map((interconnect) => {
        const startPoint = points[interconnect.start_point];
        const endPoint = points[interconnect.end_point];
        const startNode = startPoint?.node ? nodes[startPoint.node] : undefined;
        const endNode = endPoint?.node ? nodes[endPoint.node] : undefined;
        if (!startPoint || !endPoint) {
          return null;
        }
        return {
          searchFields: [
            interconnect.id.toString(),
            interconnect.alias,
            startPoint.name,
            startPoint.loc_name,
            startPoint.loc_id,
            endPoint.name,
            endPoint.loc_name,
            endPoint.loc_id,
          ].filter(Boolean) as string[],
          id: interconnect.id,
          type: 'interconnect',
          instance: interconnect,
          location:
            startNode && endNode
              ? [
                  [Number(startNode.lat), Number(startNode.lng)],
                  [Number(endNode.lat), Number(endNode.lng)],
                ]
              : undefined,
          displayName:
            interconnect.alias || `From ${startPoint.name} to ${endPoint.name}`,
          key: `interconnect-${interconnect.id}`,
        };
      })
      .filter(Boolean) as SearchElement[];
    const priceComponentElements: SearchElement[] = Object.values(
      priceComponents,
    ).flatMap((priceComponent) => {
      return priceComponent.points.slice(0, 0).map((pointId) => {
        const point = points[pointId];
        return {
          searchFields: [
            priceComponent.id.toString(),
            priceComponent.description,
          ],
          id: Number(priceComponent.id),
          type: 'priceComponent',
          instance: priceComponent,
          location: point.node
            ? [[Number(nodes[point.node].lat), Number(nodes[point.node].lng)]]
            : undefined,
          displayName: `${priceComponent.description} at ${point.name}`,
          key: `priceComponent-${priceComponent.id}-point-${pointId}`,
        };
      });
    });
    return [
      ...pointElements,
      ...interconnectElements,
      ...priceComponentElements,
    ];
  },
);

export const selectSearchResults = createSelector(
  [selectSearchElements, (state) => state.search.searchQuery],
  (elements, searchQuery) => {
    if (!searchQuery || searchQuery.trim().length < 3) {
      return [] as SearchElement[];
    }
    const fuse = new Fuse(elements, {
      keys: ['searchFields'],
      threshold: 0.2,
    });

    return fuse.search(searchQuery).map((result) => result.item);
  },
);

export const { searchingChanged, queryChanged } = searchSlice.actions;

export default searchSlice.reducer;
